// filename: useNotifyGtmAfterHydration.tsx
import { useEffect } from 'react';
import { notifyGtmAfterHydration } from '../helpers/gtmReadyAfterHydration';

/**
 * Checks if phone numbers are present in the DOM
 * Phone numbers are identified by elements with class 'header__call' or 'header__call__label'
 */
function checkPhoneNumbersInDOM(): boolean {
    if (typeof document === 'undefined') return false;

    // Check for phone number elements in the header
    const phoneElements = document.querySelectorAll('.header__call, .header__call__label');

    // Also check for tel: links which contain phone numbers
    const telLinks = document.querySelectorAll('a[href^="tel:"]');

    // Phone numbers are in DOM if we have either phone elements or tel links
    return phoneElements.length > 0 || telLinks.length > 0;
}

/**
 * Waits for phone numbers to be in the DOM before firing the hydration event
 * Returns a cleanup function to cancel the wait if component unmounts
 */
function waitForPhoneNumbers(maxAttempts = 50, interval = 100): { promise: Promise<void>; cleanup: () => void } {
    let checkInterval: NodeJS.Timeout | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;
    let attempts = 0;

    const promise = new Promise<void>((resolve) => {
        checkInterval = setInterval(() => {
            if (isCancelled) {
                if (checkInterval) clearInterval(checkInterval);
                return;
            }

            attempts++;

            if (checkPhoneNumbersInDOM() || attempts >= maxAttempts) {
                if (checkInterval) clearInterval(checkInterval);
                // Add a small delay to ensure DOM is fully settled
                timeoutId = setTimeout(() => {
                    if (!isCancelled) resolve();
                }, 50);
            }
        }, interval);
    });

    const cleanup = () => {
        isCancelled = true;
        if (checkInterval) clearInterval(checkInterval);
        if (timeoutId) clearTimeout(timeoutId);
    };

    return { promise, cleanup };
}

export function useNotifyGtmAfterHydration(eventName = 'react_hydrated'): void {
    useEffect(() => {
        // Wait for phone numbers to be in DOM before firing the event
        // This ensures phone numbers are present before react_hydrated event fires
        const { promise, cleanup } = waitForPhoneNumbers();

        promise.then(() => {
            // Runs after initial client render/hydration and phone numbers are confirmed in DOM
            notifyGtmAfterHydration(eventName);
        });

        // Cleanup function to prevent memory leaks if component unmounts
        return cleanup;
        // No dependencies: fire once per component mount
    }, [eventName]);
}
